// WB6 — the join between a ROUTE INSTANCE and the live STREAMS that carry it.
//
// The Streams page used to draw those two as separate tables: "Running streams" (route instances)
// and "Live streams" (stream sessions), with a "Map" button in the second one that opened a drawer
// showing the first. An operator debugging a silent call had to read a stream id out of one table
// and hunt for it in the other, and the relationship between them — which is the only thing that
// explains why a stream exists at all — lived in a modal.
//
// They are one table now, and this module is the join that makes that possible. Every function here
// is pure so the matching can be pinned by tests: the lookup is an IDENTITY (a stream id against the
// instance's own source/output/per-hop stream ids), never a guess on time or tool name, because the
// moment two calls of the same route overlap a guess is wrong and still looks right.

import type { RouteInstance } from '../api/schemas';

/** Where a stream id appears inside a route instance — which is what makes the map of a
 *  stream a FACT rather than a guess. The hop it names is the one that PRODUCES it (its
 *  `output_stream_id`), else the one that reads it, else null for the route's own ends. */
export interface StreamPlacement {
  instance: RouteInstance;
  /** The hop index this stream is attached to, or null when it is the route source/output. */
  hop: number | null;
  /** How the stream relates to the route — shown as the drawer's subtitle. */
  role: string;
}

export function placeStream(streamId: string, instances: RouteInstance[]): StreamPlacement | null {
  for (const inst of instances) {
    for (const h of inst.hops) {
      if (h.output_stream_id === streamId)
        return { instance: inst, hop: h.index, role: `output of hop #${h.index}` };
    }
    for (const h of inst.hops) {
      if (h.input_stream_id === streamId)
        return { instance: inst, hop: h.index, role: `input of hop #${h.index}` };
    }
    if (inst.source_stream_id === streamId) return { instance: inst, hop: null, role: 'the route source' };
    if (inst.output_stream_id === streamId) return { instance: inst, hop: null, role: 'the route output' };
  }
  return null;
}

/** One stream leg of a route instance: a stream id, and what it is FOR in that route. */
export interface StreamLeg {
  streamId: string;
  /** The hop that produces this stream, or null for a route end. */
  hop: number | null;
  /** Human sentence for the Role column. */
  role: string;
  /** The canonical tool of the producing hop, when there is one. */
  tool?: string;
  /** True when this leg is what the caller listens to (the route's DOWN end). */
  isOutput: boolean;
}

/** Every stream an instance carries, in PIPELINE order: source, then each hop's output by hop
 *  index, then the route output when no hop already claimed it.
 *
 *  Listing legs from the INSTANCE rather than from the live stream list is deliberate: a leg whose
 *  stream has already closed still belongs to the route, and dropping it would make a torn-down
 *  half of a chain look like a chain that never had that half. The caller enriches each leg with
 *  whatever `GET /streams` still knows about it. */
export function instanceLegs(inst: RouteInstance): StreamLeg[] {
  const legs: StreamLeg[] = [];
  const seen = new Set<string>();
  const outputId = inst.output_stream_id ?? null;

  const push = (id: string | null | undefined, hop: number | null, role: string, tool?: string) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    legs.push({ streamId: id, hop, role, tool, isOutput: !!outputId && id === outputId });
  };

  // A hop's output is a better answer than "an end", so hop outputs claim their ids first — the
  // source id is also hop #0's input, and calling that leg "the source" is the useful reading.
  const hopOutputs = new Set(inst.hops.map((h) => h.output_stream_id).filter(Boolean));
  if (inst.source_stream_id && !hopOutputs.has(inst.source_stream_id)) {
    push(inst.source_stream_id, null, 'source');
  }
  for (const h of [...inst.hops].sort((a, b) => a.index - b.index)) {
    push(h.output_stream_id, h.index, `output of hop #${h.index}`, h.canonical_tool);
  }
  push(outputId, null, 'route output');
  return legs;
}

/** Split a stream list into the ones some instance carries and the ones none does.
 *
 *  The leftovers are not noise: a plain streaming tool call (no route) produces exactly such a
 *  stream, and a page that only showed route legs would have silently stopped listing them. */
export function splitByRoute<T extends { id: string }>(
  streams: T[],
  instances: RouteInstance[],
): { attached: Map<string, T[]>; unattached: T[] } {
  const attached = new Map<string, T[]>();
  const unattached: T[] = [];
  for (const s of streams) {
    const found = placeStream(s.id, instances);
    if (!found) {
      unattached.push(s);
      continue;
    }
    const key = found.instance.instance_id;
    const list = attached.get(key);
    if (list) list.push(s);
    else attached.set(key, [s]);
  }
  return { attached, unattached };
}

/** The haystack a route row is searched over: its name, description, instance id, hop tools and
 *  the ids of every stream it carries. Lower-cased once so the filter is a plain `includes`. */
export function routeHaystack(parts: (string | null | undefined)[]): string {
  return parts
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .join(' ')
    .toLowerCase();
}

/** Does a haystack match the user's query? An empty query matches everything, and a multi-word
 *  query is an AND of its words — typing "call degraded" should narrow, not widen. */
export function matchesQuery(haystack: string, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  return words.every((w) => haystack.includes(w));
}
